// Service Report Service - Queries for service/tip tracking
import { supabase } from '../supabaseClient';
import { format, startOfMonth, endOfMonth, subDays } from 'date-fns';

/**
 * Service report item - represents a paid appointment with services and tip
 */
export interface ServiceReportItem {
  id: string;
  appointment_date: string;
  start_time: string;
  customer_name: string;
  customer_email: string | null;
  staff_id: string;
  staff_name: string;
  services: {
    name: string;
    price: number;
    duration: number;
  }[];
  total_price: number;
  tip_amount: number;
  tip_percentage: number | null;
}

/**
 * Staff service summary
 */
export interface StaffServiceSummary {
  staff_id: string;
  staff_name: string;
  staff_photo: string | null;
  total_appointments: number;
  total_revenue: number;
  total_tips: number;
  appointments: ServiceReportItem[];
}

/**
 * Day summary for crew view
 */
export interface DayServiceSummary {
  date: string;
  dateLabel: string;
  appointments: ServiceReportItem[];
  total_services: number;
  total_tips: number;
}

/**
 * Fetch paid appointments with services and tips for admin report
 */
export const getServiceReports = async (
  startDate: Date,
  endDate: Date,
  staffId?: string
): Promise<StaffServiceSummary[]> => {
  try {
    // Build query for paid appointments
    let query = supabase
      .from('appointments')
      .select(`
        id,
        start_time,
        end_time,
        payment_status,
        client_id,
        staff_id,
        user_profiles!appointments_client_id_fkey (
          full_name,
          email
        ),
        team!appointments_staff_id_fkey (
          id,
          name,
          photo
        ),
        appointment_services (
          price,
          duration,
          services (
            name
          )
        ),
        tips (
          amount,
          tip_percentage,
          status
        )
      `)
      .eq('payment_status', 'paid')
      .gte('start_time', startDate.toISOString())
      .lte('start_time', endDate.toISOString())
      .order('start_time', { ascending: false });

    // Filter by staff if provided
    if (staffId) {
      query = query.eq('staff_id', staffId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching service reports:', error);
      throw new Error(`Failed to fetch service reports: ${error.message}`);
    }

    // Group by staff
    const staffMap = new Map<string, StaffServiceSummary>();

    (data || []).forEach((appointment: any) => {
      const staff = appointment.team;
      if (!staff) return; // Skip if no staff assigned

      const staffId = staff.id;

      // Initialize staff summary if not exists
      if (!staffMap.has(staffId)) {
        staffMap.set(staffId, {
          staff_id: staffId,
          staff_name: staff.name || 'Unknown',
          staff_photo: staff.photo || null,
          total_appointments: 0,
          total_revenue: 0,
          total_tips: 0,
          appointments: [],
        });
      }

      const staffSummary = staffMap.get(staffId)!;

      // Parse services
      const services = (appointment.appointment_services || []).map((as: any) => ({
        name: as.services?.name || 'Unknown Service',
        price: as.price || 0,
        duration: as.duration || 0,
      }));

      const totalPrice = services.reduce((sum: number, s: any) => sum + s.price, 0);

      // Get tip (use first succeeded tip if multiple)
      // Handle case where tips might be single object or array
      const tipsArray = Array.isArray(appointment.tips)
        ? appointment.tips
        : appointment.tips
          ? [appointment.tips]
          : [];
      const succeededTip = tipsArray.find((t: any) => t.status === 'succeeded');
      const tipAmount = succeededTip?.amount || 0;
      const tipPercentage = succeededTip?.tip_percentage || null;

      // Create report item
      const reportItem: ServiceReportItem = {
        id: appointment.id,
        appointment_date: format(new Date(appointment.start_time), 'yyyy-MM-dd'),
        start_time: appointment.start_time,
        customer_name: appointment.user_profiles?.full_name || 'Guest',
        customer_email: appointment.user_profiles?.email || null,
        staff_id: staffId,
        staff_name: staff.name || 'Unknown',
        services,
        total_price: totalPrice,
        tip_amount: tipAmount,
        tip_percentage: tipPercentage,
      };

      // Update staff summary
      staffSummary.total_appointments += 1;
      staffSummary.total_revenue += totalPrice;
      staffSummary.total_tips += tipAmount;
      staffSummary.appointments.push(reportItem);
    });

    // Convert map to array and sort by total revenue
    return Array.from(staffMap.values()).sort((a, b) => b.total_revenue - a.total_revenue);
  } catch (err) {
    console.error('Error in getServiceReports:', err);
    throw err;
  }
};

/**
 * Fetch services for a specific crew member (for crew dashboard)
 */
export const getCrewServices = async (
  staffId: string,
  date?: Date
): Promise<DayServiceSummary[]> => {
  try {
    // If date provided, fetch that specific date
    // Otherwise fetch last 3 days
    let startDate: Date;
    let endDate: Date;

    if (date) {
      startDate = new Date(date);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(date);
      endDate.setHours(23, 59, 59, 999);
    } else {
      endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
      startDate = subDays(endDate, 2); // Last 3 days including today
      startDate.setHours(0, 0, 0, 0);
    }

    const { data, error } = await supabase
      .from('appointments')
      .select(`
        id,
        start_time,
        payment_status,
        client_id,
        user_profiles!appointments_client_id_fkey (
          full_name
        ),
        appointment_services (
          price,
          duration,
          services (
            name
          )
        ),
        tips (
          amount,
          tip_percentage,
          status
        )
      `)
      .eq('staff_id', staffId)
      .eq('payment_status', 'paid')
      .gte('start_time', startDate.toISOString())
      .lte('start_time', endDate.toISOString())
      .order('start_time', { ascending: false });

    if (error) {
      console.error('Error fetching crew services:', error);
      throw new Error(`Failed to fetch crew services: ${error.message}`);
    }

    // Group by date
    const dateMap = new Map<string, DayServiceSummary>();

    (data || []).forEach((appointment: any) => {
      const dateStr = format(new Date(appointment.start_time), 'yyyy-MM-dd');
      const dateLabel = format(new Date(appointment.start_time), 'EEEE, MMM d');

      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, {
          date: dateStr,
          dateLabel,
          appointments: [],
          total_services: 0,
          total_tips: 0,
        });
      }

      const daySummary = dateMap.get(dateStr)!;

      // Parse services
      const services = (appointment.appointment_services || []).map((as: any) => ({
        name: as.services?.name || 'Unknown Service',
        price: as.price || 0,
        duration: as.duration || 0,
      }));

      const totalPrice = services.reduce((sum: number, s: any) => sum + s.price, 0);

      // Get tip - handle case where tips might be single object or array
      const tipsArray = Array.isArray(appointment.tips)
        ? appointment.tips
        : appointment.tips
          ? [appointment.tips]
          : [];
      const succeededTip = tipsArray.find((t: any) => t.status === 'succeeded');
      const tipAmount = succeededTip?.amount || 0;

      const reportItem: ServiceReportItem = {
        id: appointment.id,
        appointment_date: dateStr,
        start_time: appointment.start_time,
        customer_name: appointment.user_profiles?.full_name || 'Guest',
        customer_email: null,
        staff_id: staffId,
        staff_name: '',
        services,
        total_price: totalPrice,
        tip_amount: tipAmount,
        tip_percentage: succeededTip?.tip_percentage || null,
      };

      daySummary.appointments.push(reportItem);
      daySummary.total_services += 1;
      daySummary.total_tips += tipAmount;
    });

    // Convert map to array and sort by date descending
    return Array.from(dateMap.values()).sort((a, b) => b.date.localeCompare(a.date));
  } catch (err) {
    console.error('Error in getCrewServices:', err);
    throw err;
  }
};

/**
 * Get overall summary stats for admin
 */
export const getServiceReportSummary = async (
  startDate: Date,
  endDate: Date
): Promise<{
  totalAppointments: number;
  totalRevenue: number;
  totalTips: number;
  avgTip: number;
}> => {
  const reports = await getServiceReports(startDate, endDate);

  const totalAppointments = reports.reduce((sum, r) => sum + r.total_appointments, 0);
  const totalRevenue = reports.reduce((sum, r) => sum + r.total_revenue, 0);
  const totalTips = reports.reduce((sum, r) => sum + r.total_tips, 0);
  const avgTip = totalAppointments > 0 ? totalTips / totalAppointments : 0;

  return {
    totalAppointments,
    totalRevenue,
    totalTips,
    avgTip,
  };
};

/**
 * Get list of staff members for filter dropdown
 */
export const getStaffList = async (): Promise<{ id: string; name: string }[]> => {
  const { data, error } = await supabase
    .from('team')
    .select('id, name')
    .order('name');

  if (error) {
    console.error('Error fetching staff list:', error);
    return [];
  }

  return data || [];
};

/**
 * Export service reports to CSV format
 */
export const exportServiceReportsCSV = (reports: StaffServiceSummary[]): string => {
  const headers = [
    'Staff Member',
    'Date',
    'Customer',
    'Services',
    'Service Price',
    'Tip Amount',
    'Tip %',
  ];

  const rows: string[][] = [];

  reports.forEach((staff) => {
    staff.appointments.forEach((apt) => {
      rows.push([
        staff.staff_name,
        apt.appointment_date,
        apt.customer_name,
        apt.services.map((s) => s.name).join('; '),
        apt.total_price.toFixed(2),
        apt.tip_amount.toFixed(2),
        apt.tip_percentage ? `${apt.tip_percentage}%` : '-',
      ]);
    });
  });

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  return csvContent;
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
